/**
 * Блок «Небаланси»: 4 KPI (залежні від режиму) + комбо-графік (небаланс pos↑/
 * neg↓ + актуальна/фактична вартість) + ТРАНСПОНОВАНА таблиця (періоди у
 * стовпцях, показники рядками, стовпець «Всього») + інфо-повідомлення +
 * керування закриттям місяця (авто/ручне) + економічний ефект після закриття.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useState } from 'react';
import { Scale, Lock, LockOpen, Info, Coins, Wallet, CalendarClock, ArrowLeftRight, Clock, CalendarDays } from 'lucide-react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { ImbalanceChart } from './FinComboChart';
import FinHourlyTable from './FinHourlyTable';
import type { FinCol, FinRow } from './FinHourlyTable';
import type { ImbalanceBlockData, MonthCloseStatus, FinGran, FinPeriodKind } from './overviewTypes';
import { fmtUah, fmtNum2 } from './overviewTypes';

const fmtKwh = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '—' : (v >= 0 ? '+' : '−') + Math.abs(v).toLocaleString((i18n.language === 'en' ? 'en-GB' : 'uk-UA'), { maximumFractionDigits: 1 }));
const fmtPct = (v: number | null | undefined) => (v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
const colLabel = (key: string | number, gran: FinGran) =>
  gran === 'hour' ? String(Number(key)).padStart(2, '0') : `${String(key).slice(8, 10)}.${String(key).slice(5, 7)}`;

function KpiMini({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="tile p-3 flex flex-col justify-center">
      <div className="text-[10.5px] mb-1 truncate" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="font-display text-lg font-normal leading-none" style={{ color: 'var(--text)' }}>{value}</div>
      {sub && <div className="text-[10px] mt-1" style={{ color: 'var(--text-dim)' }}>{sub}</div>}
    </div>
  );
}

interface Props {
  data: ImbalanceBlockData;
  gran: FinGran;
  period: FinPeriodKind;
  monthClose: MonthCloseStatus;
  assetId: string;
  canEdit: boolean;
  onChanged: () => void;
  chartOnly?: boolean;
  kpiOnly?: boolean;
  dayDate?: string;   // ISO дня для kpiOnly-підписів (за яку добу показники)
}

export default function ImbalanceBlock({ data, gran, period, monthClose, assetId, canEdit, onChanged, chartOnly = false, kpiOnly = false, dayDate }: Props) {
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();
  const closed = monthClose.isClosed;
  const k = data.kpi;
  const month = (monthClose.closeMonth || '').slice(0, 7);
  // Година/дата під курсором на графіку — підсвічує й докручує ту саму
  // колонку в таблиці нижче (як на /dispatch/forecast-mms).
  const [activeKey, setActiveKey] = useState<string | number | null>(null);

  const toggleClose = async () => {
    if (!assetId || !month || busy) return;
    setBusy(true);
    try {
      if (monthClose.manualClosed) {
        await authFetch(`${API_BASE_URL}/financial/result/month-close?assetId=${encodeURIComponent(assetId)}&month=${month}`, { method: 'DELETE' });
      } else {
        await authFetch(`${API_BASE_URL}/financial/result/month-close`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assetId, month }),
        });
      }
      onChanged();
    } catch { /* no-op */ } finally { setBusy(false); }
  };

  const kpi4 = period === 'day'
    ? { label: t('ak_fin_imbalances_since_month_start'), value: fmtUah(k.accumFromMonthStartUah) }
    : closed
      ? { label: t('ak_fin_economic_effect'), value: fmtUah(k.economicEffectUah), sub: k.economicEffectPct == null ? undefined : fmtPct(k.economicEffectPct) }
      : { label: t('ak_fin_economic_effect'), value: '—', sub: t('ak_fin_after_the_month_is_closed') };

  const rows = data.rows;  // усі години 01-24 (або всі дати) — таблиця фіксована
  const hasData = data.rows.some((r) => r.imbalanceMwh != null || r.currentCostUah != null || r.actualCostUah != null);
  const cols: FinCol[] = [
    ...rows.map((r) => ({ key: String(r.key), label: colLabel(r.key, gran) })),
    { key: 'sum', label: t('ak_fin_total'), strong: true },
  ];
  const tableRows: FinRow[] = [
    {
      name: t('ak_fin_imbalance_kwh'),
      Icon: Scale,
      cells: [
        ...rows.map((r) => ({ v: fmtKwh(r.imbalanceKwh), color: (r.imbalanceMwh ?? 0) >= 0 ? 'var(--ok)' : 'var(--warn)' })),
        { v: fmtKwh(data.totals.imbalanceKwh) },
      ],
    },
    {
      name: t('ak_fin_current_cost_uah'), Icon: Coins, strong: true,
      cells: [...rows.map((r) => ({ v: fmtNum2(r.currentCostUah) })), { v: fmtNum2(data.totals.currentCostUah) }],
    },
    ...(closed ? [
      { name: t('ak_fin_actual_cost_uah'), Icon: Wallet, cells: [...rows.map((r) => ({ v: fmtNum2(r.actualCostUah) })), { v: fmtNum2(data.totals.actualCostUah) }] } as FinRow,
      { name: t('ak_fin_difference_uah'), Icon: ArrowLeftRight, color: 'var(--text-muted)', cells: [...rows.map((r) => ({ v: fmtNum2(r.diffUah) })), { v: (data.totals.currentCostUah != null && data.totals.actualCostUah != null) ? fmtNum2(data.totals.currentCostUah - data.totals.actualCostUah) : '—' }] } as FinRow,
    ] : []),
  ];

  // Компактний режим (дашборд): KPI-рядок + графік у скляній плитці із заголовком
  // ВСЕРЕДИНІ (вирівнювання компоновки). Без таблиці, пояснень і керування місяцем.
  // KPI-режим: 4 ОКРЕМІ маленькі плитки 2×2 у стилі верхніх KPI-чіпів
  // (іконка-квадрат + підпис/значення, вертикально відцентровано), що заповнюють
  // простір однієї картки. Сусідні .tile — не плитка-на-плитці.
  if (kpiOnly) {
    const dayFmt = dayDate ? dayDate.slice(0, 10).split('-').reverse().join('.') : '';
    const mcm = (monthClose.closeMonth || '').slice(0, 10);
    const monthFmt = mcm ? new Date(mcm + 'T12:00:00').toLocaleDateString((i18n.language === 'en' ? 'en-GB' : 'uk-UA'), { month: 'long', year: 'numeric' }) : t('ak_fin_current_month');
    // Фіксоване оформлення чіпа: іконка ↖ ліворуч-угорі + заголовок (до 2 рядків);
    // фінансовий показник — по центру; період (за … + дд.мм.рррр) — знизу по центру.
    const KTile = ({ Icon, label, value, sub }: { Icon: typeof Coins; label: string; value: string; sub: string }) => (
      <div className="tile p-3.5 flex flex-col h-full">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-tile-sm flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}>
            <Icon size={16} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          </div>
          <span className="text-[11px] leading-tight pt-0.5" style={{ color: 'var(--text-muted)' }}>{label}</span>
        </div>
        <div className="flex-1 flex items-center justify-center min-h-0 py-1">
          <span className="font-display text-[20px] font-normal leading-none text-center truncate" style={{ color: 'var(--text)' }}>{value}</span>
        </div>
        <div className="text-[10px] text-center truncate" style={{ color: 'var(--text-dim)' }}>{sub}</div>
      </div>
    );
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-3 h-full">
        <KTile Icon={Coins} label={`${t('ak_fin_planned_income')} · ${t('ak_fin_without_vat')}`} value={fmtUah(k.plannedIncomeUah)} sub={dayFmt ? t('ak_fin_for_the_day', { d: dayFmt }) : t('ak_fin_for_the_day_2')} />
        <KTile Icon={Scale} label={closed ? t('ak_fin_imbalance_result_actual') : t('ak_fin_current_imbalance_result')} value={fmtUah(k.currentResultUah)} sub={dayFmt ? t('ak_fin_as_of', { d: dayFmt }) : t('ak_fin_as_of_today')} />
        <KTile Icon={Wallet} label={`${t('ak_fin_financial_result')} · ${t('ak_fin_without_vat')}`} value={fmtUah(k.financialResultUah)} sub={dayFmt ? t('ak_fin_for_the_day', { d: dayFmt }) : t('ak_fin_for_the_day_2')} />
        <KTile Icon={CalendarClock} label={t('ak_fin_imbalances_since_month_start')} value={fmtUah(k.accumFromMonthStartUah)} sub={monthFmt} />
      </div>
    );
  }

  if (chartOnly) {
    const act = (r: any) => (r.imbalanceMwh != null && r.imbalanceMwh !== 0) || (r.currentCostUah != null && r.currentCostUah !== 0);
    let lo = 0, hi = data.rows.length - 1;
    if (gran === 'hour') { while (lo < hi && !act(data.rows[lo])) lo++; while (hi > lo && !act(data.rows[hi])) hi--; }
    const cr = data.rows.slice(lo, hi + 1);
    return (
      <div className="tile p-4 flex flex-col h-full">
        <div className="flex items-center gap-2 mb-2 shrink-0">
          <Scale size={15} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{t('ak_fin_imbalances')}</h3>
        </div>
        <div className="flex-1 min-h-0">
          {hasData ? <ImbalanceChart rows={cr} gran={gran} showActual={closed} compact height="100%" /> : <Empty />}
        </div>
      </div>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Scale size={17} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          <h3 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>{t('ak_fin_imbalances')}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full"
            style={{ color: closed ? 'var(--ok)' : 'var(--text-dim)', border: `1px solid ${closed ? 'var(--ok)' : 'var(--line)'}` }}>
            {closed ? <Lock size={12} /> : <LockOpen size={12} />}
            {closed ? (monthClose.mode === 'auto' ? t('ak_fin_month_closed_auto') : t('ak_fin_month_closed')) : t('ak_fin_month_open')}
          </span>
          {canEdit && (
            <button onClick={toggleClose} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-lg cursor-pointer transition-colors disabled:opacity-50"
              style={{ color: 'var(--text)', background: 'var(--surface-2)', border: '1px solid var(--line)' }}
              title={t('ak_fin_manually_close_reopen_the_month')}>
              {monthClose.manualClosed ? <LockOpen size={13} /> : <Lock size={13} />}
              {monthClose.manualClosed ? t('ak_fin_reopen_month') : t('ak_fin_close_month')}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <KpiMini label={`${t('ak_fin_planned_income')} · ${t('ak_fin_without_vat')}`} value={fmtUah(k.plannedIncomeUah)} />
        <KpiMini label={closed ? t('ak_fin_imbalance_result_actual') : t('ak_fin_current_imbalance_result')} value={fmtUah(k.currentResultUah)} />
        <KpiMini label={`${t('ak_fin_financial_result')} · ${t('ak_fin_without_vat')}`} value={fmtUah(k.financialResultUah)} />
        <KpiMini label={kpi4.label} value={kpi4.value} sub={kpi4.sub} />
      </div>

      <div className="tile p-4 mb-3">
        {hasData ? <ImbalanceChart rows={data.rows} gran={gran} showActual={closed} onActiveKeyChange={setActiveKey} /> : <Empty />}
      </div>

      {rows.length > 0 ? (
        <FinHourlyTable firstLabel={gran === 'hour' ? t('ak_fin_metric_hour') : t('ak_fin_metric_date')} cols={cols} rows={tableRows} minWidth={720} activeColKey={activeKey} cornerIcon={gran === 'hour' ? Clock : CalendarDays} plotLeft={66} plotRight={74} />
      ) : (
        <div className="tile p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('ak_fin_no_data_for_the_period')}</div>
      )}

      <div className="flex items-start gap-2 text-[11.5px] mt-3" style={{ color: 'var(--text-dim)' }}>
        <Info size={13} strokeWidth={1.75} className="mt-0.5 shrink-0" />
        <span>
          {closed
            ? t('ak_fin_after_the_month_is_closed_the_actual_i')
            : t('ak_fin_before_the_month_is_closed_the_platfor')}
        </span>
      </div>
    </section>
  );
}

function Empty() {
  const { t } = useTranslation();
  return <div className="flex items-center justify-center text-sm" style={{ height: 240, color: 'var(--text-muted)' }}>{t('ak_fin_no_data_for_the_chart')}</div>;
}
